import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactDropdown from 'shared/design-system/components/TrueImpactDropdown';
import TrueImpactCollapse from 'shared/design-system/components/TrueImpactCollapse';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { NPOUser } from './api';
import {
  RequestState,
  RequestStateSuccess,
} from 'shared/auth/hooks/useRequestState';
import { Program } from '../../Reports/NewReportDialog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/pro-light-svg-icons';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import ContributionEligibilitySelector, {
  ContributionEligibility,
} from '../ContributionEligibility/ContributionEligibilitySelector';
import { useFeatureFlags } from 'shared/providers/FeatureFlags/useFeatureFlags';
import ProgramNameDonorField from '../ProgramDonorName/ProgramNameDonorField';
import ProgramDonorNameModal from '../ProgramDonorName/ProgramDonorNameModal';
import { useProgramNameDonorFlag } from '../ProgramDonorName/useProgramNameDonorFlag';

interface NewProgramContentProps {
  onDonorNameKept?: () => unknown;
  allNPOUsers: RequestState<{
    allUsers: NPOUser[];
  }>;
  programsList: RequestStateSuccess<{
    allPrograms: Program[];
  }>;
}

const StyledDiv = styled.div`
  max-width: 432px;
  margin-left: auto;
  margin-right: auto;
  padding: ${SPACERS.extraLarge};
  .text-label {
    margin-bottom: ${SPACERS.tiny};
  }
  .textfield {
    margin-bottom: ${SPACERS.medium};
  }
  .show-all-programs {
    cursor: pointer;
    margin-top: ${SPACERS.medium};
    color: ${COLORS.ctaPrimary};
  }
  .eligibility-field {
    margin-top: ${SPACERS.medium};
  }
  .programs-list {
    list-style: none;
    display: flex;
    flex-wrap: wrap;
    gap: ${SPACERS.tiny};
    padding: 0;
    padding-top: ${SPACERS.small};
  }
  .programs-list li {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    background: ${COLORS.greyLight};
    color: ${COLORS.blackBase};
    border: 1px solid ${COLORS.greyLight};
    border-radius: 999px;
    font-size: 12px;
    line-height: 1.4;
  }
`;

export interface NewProgramContentHandler {
  answer: () => {
    answer:
      | {
          programName: string;
          manager: NPOUser;
          contributionEligibility: ContributionEligibility | null;
        }
      | undefined;
    errorText: string | undefined;
  };
  checkDonorName: () => Promise<boolean>;
}

const NewProgramContent = forwardRef<
  NewProgramContentHandler,
  NewProgramContentProps
>(({ allNPOUsers, programsList, onDonorNameKept }, forwardRef) => {
  const isProgramProfilingEnabled = useFeatureFlags('programProfiling');
  const [name, setName] = useState('');
  const donor = useProgramNameDonorFlag(name);
  const submitPendingRef = useRef(false);
  const [showProgramsList, setShowProgramsList] = useState<boolean>(false);
  const [manager, setManager] = useState<NPOUser | undefined>(undefined);
  const [
    contributionEligibility,
    setContributionEligibility,
  ] = useState<ContributionEligibility | null>(null);

  const handleKeepName = useCallback(() => {
    donor.keepName();
    if (submitPendingRef.current) {
      submitPendingRef.current = false;
      onDonorNameKept?.();
    }
  }, [donor.keepName, onDonorNameKept]);

  const handleCorrectName = useCallback(() => {
    submitPendingRef.current = false;
    donor.closeModal();
  }, [donor.closeModal]);

  useImperativeHandle(forwardRef, () => ({
    checkDonorName: async () => {
      const needsDecision = await donor.flushCheck();
      if (needsDecision) {
        submitPendingRef.current = true;
        donor.openModal();
      }
      return needsDecision;
    },
    answer: () => {
      const programName = name;
      if (!programName || !manager) {
        return {
          answer: undefined,
          errorText: 'Please enter a valid program name and manager.',
        };
      }
      if (isProgramProfilingEnabled && !contributionEligibility) {
        return {
          answer: undefined,
          errorText: 'Please select a contribution eligibility.',
        };
      }
      return {
        answer: { programName, manager, contributionEligibility },
        errorText: undefined,
      };
    },
  }));

  const users = allNPOUsers.data?.allUsers || [];

  return (
    <StyledDiv>
      <TrueImpactText variant="body-bold" className="text-label">
        Program Name
      </TrueImpactText>
      <ProgramNameDonorField
        value={name}
        donor={donor}
        onChange={setName}
        autoFocus={true}
        className="textfield"
      />
      <ProgramDonorNameModal
        open={donor.modalOpen}
        onKeepName={handleKeepName}
        onCorrectName={handleCorrectName}
      />
      <TrueImpactText variant="body-bold" className="text-label">
        Reporting Manager
      </TrueImpactText>
      <TrueImpactDropdown
        value={manager}
        options={users}
        getId={p => p.userId}
        getLabel={p => `${p.firstName} ${p.lastName} (${p.email})`}
        native={true}
        onChange={setManager}
        onClear={undefined}
        getItemDisability={undefined}
        name="Please select program manager"
        className="dropdown-select"
      />
      {isProgramProfilingEnabled && (
        <div className="eligibility-field">
          <ContributionEligibilitySelector
            value={contributionEligibility}
            onChange={setContributionEligibility}
          />
        </div>
      )}
      {programsList.type === 'REQUEST_SUCCESS' && (
        <>
          <TrueImpactText
            allowTextSelection={false}
            className="show-all-programs"
            onClick={() => setShowProgramsList(s => !s)}
          >
            <FontAwesomeIcon icon={showProgramsList ? faEyeSlash : faEye} />{' '}
            {showProgramsList ? 'Hide ' : 'Show '}
            all active programs
          </TrueImpactText>
          <TrueImpactCollapse show={showProgramsList}>
            <ul className="programs-list">
              {programsList.data.allPrograms.map(p => (
                <li key={p.Id}>{p.name}</li>
              ))}
            </ul>
          </TrueImpactCollapse>
        </>
      )}
    </StyledDiv>
  );
});

export default NewProgramContent;
